import React from "react";
import { Play, X, Trash2, ListMusic, ShieldCheck } from "lucide-react";
import { generateFakeFilename, formatDuration } from "@/lib/fakeData";

/**
 * Playlist queue. If `embedded` is true, omits the internal header (used inside a Panel).
 */
export default function PlaylistQueue({ audio, embedded = false }) {
  const { queue, currentIndex, playFromQueue, removeFromQueue, clearQueue } = audio;

  return (
    <div data-testid="playlist-queue" className="bevel-in bg-white flex flex-col flex-1 min-h-0 overflow-hidden">
      {!embedded && (
        <div className="bg-[#ECE9D8] border-b border-[#9C9A7E] px-2 py-1 flex items-center justify-between text-[11px] font-bold">
          <span className="flex items-center gap-1.5">
            <ListMusic size={12} className="text-[#003c9e]" />
            Playlist Queue ({queue.length})
          </span>
          {queue.length > 0 && (
            <button
              data-testid="queue-clear"
              onClick={clearQueue}
              className="bevel-btn px-2 py-[1px] text-[10px] flex items-center gap-1"
            >
              <Trash2 size={9} /> Clear
            </button>
          )}
        </div>
      )}

      {embedded && queue.length > 0 && (
        <div className="flex justify-end px-1 py-1 bg-[#F5F3E8] border-b border-[#DDD6BC]">
          <button
            data-testid="queue-clear"
            onClick={clearQueue}
            className="bevel-btn px-2 py-[1px] text-[10px] flex items-center gap-1"
          >
            <Trash2 size={9} /> Clear all
          </button>
        </div>
      )}

      <div className="overflow-y-auto xp-scroll flex-1">
        {queue.length === 0 && (
          <div className="p-3 text-[11px] text-[#777] text-center">
            <ListMusic size={22} className="text-[#AAA] mx-auto mb-1" />
            <div>Your queue is empty.</div>
            <div className="text-[10px] mt-1 text-[#999] italic">Double-click a track to scan &amp; play.</div>
          </div>
        )}
        {queue.map((t, i) => {
          const isCurrent = i === currentIndex;
          return (
            <div
              key={t.id}
              data-testid={`queue-item-${i}`}
              onDoubleClick={() => playFromQueue(t.id)}
              className={`flex items-center gap-1 px-1.5 py-1 border-b border-[#F0EFE7] text-[11px] cursor-pointer ${isCurrent ? "row-selected" : i % 2 === 0 ? "bg-white" : "bg-[#F8F6EE]"} hover:bg-[#D0E4FB]`}
            >
              <span className={`text-[10px] font-mono w-5 text-center shrink-0 ${isCurrent ? "text-white" : "text-[#555]"}`}>
                {i + 1}
              </span>
              <button
                data-testid={`queue-play-${i}`}
                onClick={(e) => { e.stopPropagation(); playFromQueue(t.id); }}
                className={`w-5 h-5 shrink-0 flex items-center justify-center rounded-sm ${isCurrent ? "bg-white/20" : "bevel-btn"}`}
                aria-label="Play track"
                title="Play now"
              >
                <Play size={9} className={isCurrent ? "text-white" : "text-[#003c9e]"} fill={isCurrent ? "white" : "#003c9e"} />
              </button>
              <div className="flex-1 min-w-0">
                <div
                  className="truncate font-mono text-[10.5px] leading-tight"
                  style={{ fontFamily: '"Share Tech Mono", monospace' }}
                  title={t.title}
                >
                  {generateFakeFilename(t)}
                </div>
                <div className="truncate text-[10px] opacity-80 leading-tight">
                  {t.artist} &middot; {formatDuration(t.duration_ms)}
                </div>
              </div>
              <ShieldCheck size={11} className={`shrink-0 ${isCurrent ? "text-white" : "text-[#1F8020]"}`} />
              <button
                data-testid={`queue-remove-${i}`}
                onClick={(e) => { e.stopPropagation(); removeFromQueue(t.id); }}
                className={`w-5 h-5 shrink-0 flex items-center justify-center rounded-sm ${isCurrent ? "bg-white/20 hover:bg-white/30" : "bevel-btn"}`}
                aria-label="Remove from queue"
                title="Remove"
              >
                <X size={9} className={isCurrent ? "text-white" : "text-[#8B0A14]"} strokeWidth={3} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
